import dayjs from 'dayjs';
import { omit } from 'lodash-es';

import { copyPhasesForNewProject } from '~components/Order/PhaseDrawer/copyPhasesForNewProject';
import { Project } from '~hooks/useProjects/models';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';

/**
 * Extended Project type that includes optional runtime properties
 * that may be attached when processing forms or copying projects.
 */
interface SiteRateScheduleAddOn {
  addOn?: {
    id?: string;
    name?: string;
    [key: string]: unknown;
  };
  isNew?: boolean;
  isDeleted?: boolean;
}

interface SiteRateSchedule {
  id?: string;
  site?: { id: string; name: string };
  customerRate?: number;
  vendorRate?: number;
  driverRate?: number;
  customerAddOns?: SiteRateScheduleAddOn[];
  vendorAddOns?: SiteRateScheduleAddOn[];
  driverAddOns?: SiteRateScheduleAddOn[];
}

export interface ProjectWithFormData extends Project {
  addOnAssignments?: AddOnAssignmentSchema[];
  siteRateSchedules?: SiteRateSchedule[];
}

/**
 * Strips all IDs from a project and its nested objects for the Copy and Edit feature.
 *
 * This ensures that when a copied project is submitted, it creates NEW records
 * instead of updating the original project's data.
 *
 * CRITICAL: This function must strip IDs from:
 * - The project itself (id, projectId)
 * - projectMaterialTotals
 * - allContacts
 * - phases
 * - pickUpWayPoints
 * - dropOffWayPoints
 * - customerRate, vendorRate, driverRate
 * - addOnAssignments (AND mark them as isNew: true)
 * - siteRateSchedules (AND their nested add-ons)
 *
 * Reference IDs that should be PRESERVED (they reference other entities):
 * - account.id
 * - company.id
 * - department.id
 * - foremen[].id
 * - collaborators[].id
 * - waypoint.site.id (we want to reference the same sites)
 *
 * @param project - The project to copy (may include form data like addOnAssignments)
 * @returns A new project object with IDs stripped for creation
 */
export function stripProjectIdsForCopy(
  project: ProjectWithFormData,
): ProjectWithFormData {
  // Generate date suffix in MM-DD-YY format for name and externalId
  const dateSuffix = ` - ${dayjs().format('MM-DD-YY')}`;

  // Calculate next top of hour for start time
  const now = dayjs();
  const nextHour =
    now.minute() > 0 ? now.add(1, 'hour').startOf('hour') : now.startOf('hour');

  return {
    // IMPORTANT: The Project class uses getters for its properties.
    // Spreading a class instance only copies own enumerable properties,
    // NOT getter values from the prototype. We must explicitly include
    // all properties that are accessed via getters.
    ...omit(project, ['id', 'projectId']),

    // Explicitly preserve reference entities (these are getters on Project class)
    // These reference other entities and should NOT have their IDs stripped
    account: project.account,
    company: project.company,
    department: project.department,
    foremen: project.foremen,
    collaborators: project.collaborators,
    serviceClass: project.serviceClass,
    equipmentType: project.equipmentType,
    service: project.service,
    requester: project.requester,
    chargeToAccount: project.chargeToAccount,

    // Preserve other getter-based properties
    notes: project.notes,
    orderNotes: project.orderNotes,
    jobNotes: project.jobNotes,
    internalNotes: project.internalNotes,
    projectTerms: project.projectTerms,
    poJobNumber: project.poJobNumber,
    state: project.state,
    site: project.site,
    contact: project.contact,
    salesContact: project.salesContact,
    foremanContact: project.foremanContact,
    collaboratorContact: project.collaboratorContact,
    supervisorContact: project.supervisorContact,
    haulerRateType: project.haulerRateType,
    jobTime: project.jobTime,
    orderQuantity: project.orderQuantity,
    orderUnitOfMeasure: project.orderUnitOfMeasure,
    equipmentTypeGrossCapacity: project.equipmentTypeGrossCapacity,
    equipmentTypeGrossCapacityUnitOfMeasure:
      project.equipmentTypeGrossCapacityUnitOfMeasure,
    loadsPerTruck: project.loadsPerTruck,
    truckCount: project.truckCount,
    unitsPerHour: project.unitsPerHour,
    jobQuantity: project.jobQuantity,
    jobUnitOfMeasure: project.jobUnitOfMeasure,
    loadUnitOfMeasure: project.loadUnitOfMeasure,
    recurringLoads: project.recurringLoads,
    chaseMissingTickets: project.chaseMissingTickets,
    liveEta: project.liveEta,
    defaultDigitalTicketView: project.defaultDigitalTicketView,
    salespeople: project.salespeople,
    contractNumber: project.contractNumber,
    dispatchNumber: project.dispatchNumber,
    prevailingWage: project.prevailingWage,
    applyRateChangesToExistingOrders: project.applyRateChangesToExistingOrders,
    vendorPayCommissionPercentage: project.vendorPayCommissionPercentage,
    driverPayCommissionPercentage: project.driverPayCommissionPercentage,
    materialRateItemized: project.materialRateItemized,
    taxRate: project.taxRate,
    taxFreight: project.taxFreight,
    taxMaterial: project.taxMaterial,
    taxAddOns: project.taxAddOns,
    taxRateSchedule: project.taxRateSchedule,
    expiresAt: project.expiresAt,
    customerRateType: project.customerRateType,
    customerRateValue: project.customerRateValue,
    vendorRateType: project.vendorRateType,
    vendorRateValue: project.vendorRateValue,
    driverRateType: project.driverRateType,
    driverRateValue: project.driverRateValue,

    // Append date suffix to name and externalId
    name: project.name ? `${project.name}${dateSuffix}` : project.name,
    externalId: project.externalId
      ? `${project.externalId}${dateSuffix}`
      : project.externalId,

    // Reset start date to today, start time to next top of hour
    // End date/time (expiresAt) remains unchanged from original
    effectiveAt: nextHour.toISOString(),

    // Strip IDs from project material totals
    // IMPORTANT: ProjectMaterialTotal is also a class with getters, so we must explicitly copy properties
    projectMaterialTotals: project.projectMaterialTotals?.map((materialTotal) => ({
      // Don't include id - we want to create new material totals
      material: materialTotal.material,
      unitOfMeasure: materialTotal.unitOfMeasure,
      totalQuantity: materialTotal.totalQuantity,
      // The pickup site is a reference to an existing Site, like material, so
      // the copy keeps it. (The per-row phase link is dropped here, which
      // predates the pickup site — see TRE-16142.)
      site: materialTotal.site,
    })),

    // Strip IDs from contacts
    // IMPORTANT: ExtendedContactItem is a class with getters, so we must explicitly copy properties
    allContacts: project.allContacts?.map((contact) => ({
      // Don't include id - we want to create new contacts
      name: contact.name,
      phone: contact.phone,
      email: contact.email,
      type: contact.type,
    })),

    phases: project.phases ? copyPhasesForNewProject(project.phases) : undefined,

    // Strip IDs from waypoints (but preserve site.id - we reference the same sites)
    // IMPORTANT: WayPoint is also a class with getters, so we must explicitly copy properties
    pickUpWayPoints: project.pickUpWayPoints?.map((waypoint) => ({
      // Don't include waypoint id - we want to create new waypoints
      type: waypoint.type,
      ordinality: waypoint.ordinality,
      contact: waypoint.contact,
      site: waypoint.site, // Preserve site reference (we want to use the same site)
    })),
    dropOffWayPoints: project.dropOffWayPoints?.map((waypoint) => ({
      // Don't include waypoint id - we want to create new waypoints
      type: waypoint.type,
      ordinality: waypoint.ordinality,
      contact: waypoint.contact,
      site: waypoint.site, // Preserve site reference (we want to use the same site)
    })),

    // Preserve rate card references - the id here is a reference to an existing Rate entity
    // (like account.id), not an ID that needs to be stripped.
    // IMPORTANT: RateBasicWithValue is a class with getters, so we must explicitly copy properties
    customerRate: project.customerRate
      ? {
          id: project.customerRate.id,
          name: project.customerRate.name,
          type: project.customerRate.type,
          rate: project.customerRate.rate,
          addOns: project.customerRate.addOns,
        }
      : undefined,
    vendorRate: project.vendorRate
      ? {
          id: project.vendorRate.id,
          name: project.vendorRate.name,
          type: project.vendorRate.type,
          rate: project.vendorRate.rate,
          addOns: project.vendorRate.addOns,
        }
      : undefined,
    driverRate: project.driverRate
      ? {
          id: project.driverRate.id,
          name: project.driverRate.name,
          type: project.driverRate.type,
          rate: project.driverRate.rate,
          addOns: project.driverRate.addOns,
        }
      : undefined,

    // Note: materialRates are handled separately via fetch + form patch in ProjectDialog.
    // The form's useEffect strips IDs when isCopyAndEdit is true.

    // CRITICAL FIX: Strip IDs from add-on assignments and mark as new
    // This prevents PATCH calls to original project's add-ons
    addOnAssignments: project.addOnAssignments
      ?.filter((assignment) => !assignment.isDeleted) // Don't copy deleted add-ons
      ?.map((assignment) => ({
        ...assignment,
        addOn: assignment.addOn ? { ...assignment.addOn, id: undefined } : undefined,
        isNew: true, // Mark as new so POST is called instead of PATCH
        isDeleted: false,
      })) as AddOnAssignmentSchema[] | undefined,

    // CRITICAL FIX: Strip IDs from site rate schedules and their nested add-ons
    // This prevents PATCH calls to original project's site rate schedules
    siteRateSchedules: project.siteRateSchedules?.map((schedule) => ({
      ...omit(schedule, ['id']),
      // Strip and mark customer add-ons as new
      customerAddOns: schedule.customerAddOns
        ?.filter((a) => !a.isDeleted)
        ?.map((a) => ({
          ...a,
          addOn: a.addOn ? { ...a.addOn, id: undefined } : undefined,
          isNew: true,
          isDeleted: false,
        })),
      // Strip and mark vendor add-ons as new
      vendorAddOns: schedule.vendorAddOns
        ?.filter((a) => !a.isDeleted)
        ?.map((a) => ({
          ...a,
          addOn: a.addOn ? { ...a.addOn, id: undefined } : undefined,
          isNew: true,
          isDeleted: false,
        })),
      // Strip and mark driver add-ons as new
      driverAddOns: schedule.driverAddOns
        ?.filter((a) => !a.isDeleted)
        ?.map((a) => ({
          ...a,
          addOn: a.addOn ? { ...a.addOn, id: undefined } : undefined,
          isNew: true,
          isDeleted: false,
        })),
    })),
  } as unknown as ProjectWithFormData;
}
